import { useState, type ReactNode } from 'react';
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import { useToast } from '../../contexts/ToastContext';
import { friendlyApiErrorMessage } from '../../utils/composerErrors';
import type { StepState } from '../../types/runExplorer';

/**
 * Pieces the Run Explorer's sections share (task 1151). `getStatusBadgeClass`,
 * `CopyButton` and `runErrorText` moved here from `CollaborationDetailView`,
 * which the Run Explorer retired.
 */

export function getStatusBadgeClass(status: string) {
  switch (status) {
    case 'completed':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case 'running':
    case 'in_progress':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    case 'failed':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    case 'skipped':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    case 'waiting':
    case 'waiting_for_input':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
    case 'pending':
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
  }
}

/**
 * The text shown for a stored run or step error (task 1145): the same sentence
 * the Assistant shows for a known failure code (`credential_expired`,
 * `invalid_reply: <sentence>`, ...), otherwise the stored string unchanged.
 */
export function runErrorText(error: string): string {
  return friendlyApiErrorMessage(error) ?? error;
}

export function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const toast = useToast();

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${label} copied to clipboard`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="inline-flex items-center p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
      title={`Copy ${label}`}
      aria-label={`Copy ${label}`}
    >
      {copied ? (
        <CheckIcon className="h-3.5 w-3.5 text-green-500" />
      ) : (
        <ClipboardDocumentIcon className="h-3.5 w-3.5" />
      )}
    </button>
  );
}

/** `412ms`, `1.52s`, `41.2s`, `3m 03s`. */
export function formatMs(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 10_000) return `${(ms / 1000).toFixed(2)}s`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
}

/** Wall-clock time of day with milliseconds, in the viewer's time zone. */
export function formatClock(iso: string | null | undefined, withMs = false): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const base = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  return withMs ? `${base}.${String(date.getMilliseconds()).padStart(3, '0')}` : base;
}

export const formatCount = (n: number): string => n.toLocaleString();

/** How each step state reads, on the path, the timeline and the legend. */
export const STEP_STATE_STYLES: Record<StepState, { label: string; node: string; dot: string; badge: string }> = {
  ran: {
    label: 'ran',
    node: 'border-green-500 bg-green-50 dark:bg-green-900/30',
    dot: 'bg-green-500',
    badge: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200',
  },
  'ran-with-problem': {
    label: 'ran with a problem',
    node: 'border-amber-500 bg-amber-50 dark:bg-amber-900/30',
    dot: 'bg-amber-500',
    badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200',
  },
  failed: {
    label: 'failed',
    node: 'border-red-500 bg-red-50 dark:bg-red-900/30',
    dot: 'bg-red-500',
    badge: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200',
  },
  skipped: {
    label: 'skipped by a branch',
    node: 'border-gray-300 border-dashed bg-gray-50 opacity-60 dark:border-gray-600 dark:bg-gray-800',
    dot: 'bg-gray-400',
    badge: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  },
  'not-reached': {
    label: 'not reached this turn',
    node: 'border-gray-200 bg-white opacity-40 dark:border-gray-700 dark:bg-gray-900',
    dot: 'bg-gray-200 dark:bg-gray-600',
    badge: 'bg-gray-50 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
  },
};

/** A titled block that opens on click; `onOpen` fires the first time it opens (for fetch-on-expand). */
export function Collapsible({
  title,
  meta,
  defaultOpen = false,
  onOpen,
  children,
  testId,
}: {
  title: ReactNode;
  meta?: ReactNode;
  defaultOpen?: boolean;
  onOpen?: () => void;
  children: ReactNode;
  testId?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-gray-100 dark:border-gray-700 pt-2" data-testid={testId}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => {
          if (!open) onOpen?.();
          setOpen(!open);
        }}
        className="flex w-full items-center justify-between gap-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white"
      >
        <span className="flex items-center gap-1">
          {open ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
          {title}
        </span>
        {meta && <span className="text-xs font-normal text-gray-500 dark:text-gray-400">{meta}</span>}
      </button>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
}

export function JsonBlock({ value }: { value: unknown }) {
  let text: string;
  try {
    text = typeof value === 'string' ? value : JSON.stringify(value, null, 2) ?? 'null';
  } catch {
    text = String(value);
  }
  return (
    <pre className="p-3 bg-gray-50 dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700 overflow-auto max-h-72 text-xs font-mono text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words">
      {text}
    </pre>
  );
}

/** A one-line description of a value: a short string as itself, an object by its keys. */
export function summarizeValue(value: unknown, max = 80): string {
  if (value === undefined) return '—';
  if (value === null) return 'null';
  if (typeof value === 'string') return value.length > max ? `"${value.slice(0, max)}…"` : `"${value}"`;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return `list · ${value.length} item${value.length === 1 ? '' : 's'}`;
  const keys = Object.keys(value as Record<string, unknown>);
  const shown = keys.slice(0, 4).join(', ');
  return `object · ${shown}${keys.length > 4 ? `, +${keys.length - 4}` : ''}`;
}
